<template>
  <div class='wrapper-page'>
    <modal ref="deleteMessageForm">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.delete_messages')}}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_delete')}}</p>
          </div>
          <div class='modal-footer'>
            <button class="btn btn-outline-danger float-right" :disabled="isProcessing" @click.prevent="deleteSentMessage()">
              <i class="fas fa-circle-notch fa-spin" v-show="isProcessing"></i>
              {{ $t('communication.delete_messages_button') }}
            </button>
          </div>
        </form>
      </template>
    </modal>

    <modal ref='filtersModal' class="filter-all-messages">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('shared.filter') }}
        </h5>
      </template>

      <template #body>
        <form @submit.prevent='filterRecordMessages'>
          <div class='modal-body'>
            <div class='form-group form-inline'>
              <label>{{ $t('folders_list.starred_folders')}}</label>
              <label>
                <input type='radio' v-model='starred' value='true'>
                {{ $t('folders_list.yes_way') }}
              </label>

              <label>
                <input type='radio' v-model='starred' value='false'>
                {{ $t('folders_list.no_way') }}
              </label>
              <label>
                <i class='fas fa-times' :title="$t('records_list.remove_selection')" @click.prevent='starred = null'></i>
              </label>
            </div>
            <div class="form-group">
              <label>{{ $t('communication.to') }}</label>
              <infinite-multi-select :getter="getAddressBook" v-model="toContactFilters" />
            </div>
            <div class="form-group">
              <label>
                {{ $t('communication.status') }}
              </label>
              <div>
                <multi-select :options="messageStatus" v-model="messageStatusFilters" />
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('communication.keywords') }}</label>
              <label>
                <input type="text" name="search" v-model="searchInSent" class="form-control">
              </label>
            </div>
            <div class="form-group">
              <label>{{ $t('communication.tags') }}</label>
              <infinite-multi-select :getter="getTags" v-model="tagsFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.sent_date') }}</label>
              <div class="date">
                <label>{{ $t('communication.from') }}</label>
                <datepicker
                  ref="datepicker"
                  name="document_request[sending_date]"
                  v-model="fromSendingDate"
                  input-class="form-control expiration-input"
                  :inline="false"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  :placeholder="$t('expiration.pick_date')">
                </datepicker>
              </div>
              <div class="date">
                <label>{{ $t('communication.to') }}</label>
                <datepicker
                  ref="datepicker"
                  name="document_request[sending_date]"
                  v-model="toSendingDate"
                  input-class="form-control expiration-input"
                  :inline="false"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  @selected="this.$refs.datepicker.close()"
                  :placeholder="$t('expiration.pick_date')">
                </datepicker>
              </div>
            </div>
          </div>

          <div class='modal-footer'>
            <input type='reset' class='btn btn-neutral float-right' :value="$t('folders_list.clear_filters')" @click.prevent='resetFilters' />
            <input type='submit' class='btn btn-outline-primary float-right' :value="$t('folders_list.filter')" />
          </div>
        </form>
      </template>
    </modal>

    <div class='container-fluid text-center'>
      <h1 class='page-title'>
        {{ $t('communication.interactive_notes') }} {{ (meta.total > 0) ? `(${meta.total})` : '' }}
      </h1>
      <div class="actions-on-table">
        <div class='filter'>
          <a  @click.prevent="openModal($refs.filtersModal)" href="#" class="filter-by btn btn-neutral">
            {{ $t('root.filter_all_data_by') }}
          </a>
        </div>
      </div>
      <div class="filter-results" v-if="filterMessages">
        <strong v-if="hasFilters">{{ $t('root.filter_results_for') }}</strong>
        <div v-if="starred !== null">
          <p>{{ $t('root.filters.starred') }}
            <strong v-if="starred === 'true'">{{ $t('root.filters.yes_way') }}</strong>
            <strong v-if="starred === 'false'">{{ $t('root.filters.no_way') }}</strong>
          </p>
        </div>
        <div v-if="toContactFilters.length > 0">
          <p>{{ $t('communication.filters.to') }} <strong v-for="contact in toContactFilters">{{contact.name}}</strong></p>
        </div>
        <div v-if="messageStatusFilters.length > 0">
          <p>{{ $t('communication.filters.status') }} <strong v-for="status in messageStatusFilters">{{status.name}}</strong></p>
        </div>
        <div v-if="searchInSent !== ''">
          <p>{{ $t('communication.filters.keywords') }} <strong>{{searchInSent}}</strong></p>
        </div>
        <div v-if="tagsFilters.length > 0">
          <p>{{ $t('communication.filters.tags') }} <strong v-for="tag in tagsFilters">{{tag.name}}</strong></p>
        </div>
        <div v-if="fromSendingDate !== null">
          <p>{{ $t('communication.filters.receiving_date') }} <strong>{{formatDate(fromSendingDate)}}</strong></p>
        </div>
        <div v-if="toSendingDate !== null">
          <p>{{ $t('communication.filters.until_date') }} <strong>{{formatDate(toSendingDate)}}</strong></p>
        </div>

        <a v-if="hasFilters" @click.prevent="resetFilters" href="#">{{ $t('records_list.clear_filters') }}</a>
      </div>
      <div v-if='recordMessages.length === 0 && !loading' class="empty-list-wrapper">
        <img src="../../assets/empty_states/envelope.svg" />
        <p>{{$t('communication.message_empty_state')}}</p>
      </div>
      <div class="table_wrapper" v-else-if='recordMessages.length > 0 && !loading'>
        <div :class="['bulk-container', { 'hide': bulkOpMessages.length === 0  }]">
          <span>{{ bulkOpMessages.length }} {{ $t('root.record_messages_selected') }}</span>
          <ul>
            <li>
              <a href="#" class="dropdown-item" @click.prevent="$refs.bulkTagMessages.open(null, null, null, null, bulkOpMessages)">
                <i class="fas fa-tag"></i>
                {{ $t('communication.tags')}}
              </a>
              <bulk-tag-messages
                ref="bulkTagMessages"
                @success="addTagsToBulkMessages()">
              </bulk-tag-messages>
            </li>
            <li>
              <a @click.prevent='openDeleteMessageModal(bulkOpMessages)' href="#" rel="nofollow" class="dropdown-item">
                <i class="far fa-trash-alt"></i>
                {{ $t('communication.delete')}}
              </a>
            </li>
          </ul>
        </div>
        <table class="table">
          <thead>
          <tr>
            <th><input type='checkbox' @input='selectAll' /></th>
            <th></th>
            <th></th>
            <th>{{ $t('communication.from')}}</th>
            <th>{{ $t('communication.to')}}</th>
            <th>{{ $t('communication.file_name')}}</th>
            <th>{{ $t('communication.tags') }}</th>
            <th>{{ $t('communication.sent_on')}}</th>
            <th>{{ $t('communication.actions')}}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="message in recordMessages">
            <td cass="bulk-check">
              <input type='checkbox' :value='message.id' v-model='bulkOpMessages' />
            </td>
            <td>
              <a href='#' class="bookmarked" @click.prevent="starMessage(message)">
                <i :class="['fa-star', {'far': !message.starred, 'fas': message.starred }]"></i>
              </a>
            </td>
            <td>
              <router-link v-if="unreadStats(message.id) > 0" :to="{name: 'record_message_show', params: { id: message.id, referrer: $route.name }}" class="unread-messages-here">
                <i class="fas fa-envelope" v-show="unreadStats(message.id)"></i>
                <span v-show="unreadStats(message.id) === 1">
                      {{ $t('record_message.unread_new_message_count', {count: unreadStats(message.id)}) }}
                </span>
                <span v-show="unreadStats(message.id) > 1">
                      {{ $t('record_message.unread_new_messages_count', {count: unreadStats(message.id)}) }}
                </span>
              </router-link>
            </td>
            <td>
              <router-link :to="{name: 'record_message_show', params: { id: message.id, referrer: $route.name }}">
                {{ message.from_user.full_name }} - {{ message.from_user.email }}
              </router-link>
            </td>
            <td class="sent_to">
              {{ sentTo(message) }}
            </td>
            <td>
              <router-link v-if="message.record.id==message.record.current_version.id" :to="{ name: 'record', params: { id: message.record_id } }">
                {{ message.record.filename }}
              </router-link>
              <router-link v-else :to="{ name: 'record_show_versioned', params: { record_id: message.record.current_version.id, id: message.record_id } }">
                {{ message.record.filename }}
              </router-link>
            </td>
            <td>
              <tag-manager :message="message" message-type="recordMessage" @success='getRecordMessages()' />
            </td>
            <td>{{ formatDate(message.created_at) }}</td>
            <td class="dropdown-column">
              <div class="dropdown">
                <button aria-expanded="false" aria-haspopup="true" data-toggle="dropdown" type="button" class="btn dropdown-toggle">· · ·</button>
                <div class="dropdown-menu dropdown-menu-right">
                  <ul>
                    <li>
                      <a @click.prevent='openDeleteMessageModal([message.id])' href="#" rel="nofollow" class="dropdown-item">
                        <i class="far fa-trash-alt"></i>
                        {{ $t('communication.delete')}}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="empty-list-wrapper" v-else-if='recordMessages.length === 0 && !hasFilters && !loading'>
        <p>{{ $t('root.no_data') }}</p>
      </div>

      <div class="empty-list-wrapper" v-else-if='recordMessages.length === 0 && hasFilters && !loading'>
        <p>{{ $t('root.no_data_filters') }}</p>
        <a href="#" @click.prevent="$refs.filtersModal.reset">{{ $t('root.clear_filters') }}</a>
      </div>
      <loading v-if="loading" />
      <pagination :meta='meta' v-show="!loading"/>
    </div>
  </div>
</template>

<script>
import _modal from '../../components/_modal'
import _pagination from "../../components/_pagination"
import _loading from '../../components/_loading'
import tag_manager from "../../components/tags/_message_tag_manager"
import bulk_tag_messages from "../../components/tags/_bulk_tag_messages"
import _multi_select from "../../components/shared/_multi_select"
import _infinite_multi_select from "../../components/shared/_infinite_multi_select"
import Datepicker from 'vuejs-datepicker'

export default {
  components: {
    'multi-select': _multi_select,
    'infinite-multi-select': _infinite_multi_select,
    'modal': _modal,
    pagination: _pagination,
    'loading': _loading,
    'tag-manager': tag_manager,
    'datepicker': Datepicker,
    'bulk-tag-messages': bulk_tag_messages
  },
  data() {
    return {
      loading: false,
      bulkOpMessages: [],
      messageIds: [],
      toContactFilters: [],
      tagsFilters: [],
      messageStatusFilters: [],
      fromSendingDate: null,
      toSendingDate: null,
      searchInSent: '',
      starred: null,
      isProcessing: false
    }
  },
  watch: {
    query() {
      this.getRecordMessages()
    }
  },
  created() {
    this.getRecordMessages()
    this.store.sendMetrics({name: 'file_communication_index', params: this.$route.params, path: this.$route.fullPath})
  },
  computed: {
    recordMessages() {
      return this.state.recordMessages || []
    },
    recordMessageStats() {
      return this.stats.recordMessageStats || []
    },
    repliesStats(){
      return this.recordMessageStats.replies || []
    },
    newMessageStats(){
      return this.recordMessageStats.new_messages || []
    },
    messageStatus() {
      return [
        { id: 'pending', name: (this.$t('communication.status_message.pending')) },
        { id: 'failed', name: (this.$t('communication.status_message.failed')) },
        { id: 'delivered', name: (this.$t('communication.status_message.delivered')) },
        { id: 'opened', name: (this.$t('communication.status_message.opened')) },
      ]
    }

  },
  methods: {
    getRecordMessages(show_loading=true) {
      this.loading = show_loading
      this.store.getCommunicationRecordMessages(this.query).then(() =>  this.loading = false).catch(() => {
          this.loading = false
      }).finally(() => {
        this.updateStatsCounters()
      })
    },
    getAddressBook(params){
      return this.listStore.getAddressBook(params)
    },
    getTags(params) {
      return this.listStore.getTags(params)
    },
    openModal(modal) {
      modal.open()
      this.store.sendMetrics({name: 'file_communication_filter_list', params: this.$route.params, path: this.$route.fullPath})
    },
    starMessage(message) {
      message.starred = !message.starred
      return this.store.updateRecordMessage(message.record.id, message.id, { record_message_sent: {user_ids: [], contact_ids: []}, record_message: { starred: message.starred}}).then(() => {
        this.store.sendMetrics({name: 'file_communication_star_message', params: this.$route.params, path: this.$route.fullPath})
        this.getRecordMessages(false)
      })
    },
    removeConnectedTag(tag, recordMessageId) {
      this.store.sendMetrics({name: 'file_communication_remove_tag_from_message', params: this.$route.params, path: this.$route.fullPath})
      this.store.removeConnectedTag(tag.id, { sent_email_id: recordMessageId }).then(() => {
        this.getRecordMessages(false)
      })
    },
    filterMessages() {
      return this.toContactFilters.length > 0 ||
        this.tagsFilters.length > 0 || this.messageStatusFilters.length > 0 || this.fromSendingDate !== null ||
        this.toSendingDate !== null || this.searchInSent !== '' || this.starred !== null
    },
    selectAll(e) {
      this.bulkOpMessages = e.target.checked ? this.recordMessages.map(i => i.id) : []
    },
    closeDatePicker() {
      this.$refs.datepicker.close()
    },
    filtersOn() {
      this.$refs.filtersModal.close()
    },
    resetFilters() {
      this.toContactFilters= []
      this.tagsFilters = []
      this.messageStatusFilters = []
      this.fromSendingDate = null
      this.toSendingDate = null
      this.searchInSent = ''
      this.starred = null
      this.$router.push({ query: null})
      this.$refs.filtersModal.close()
    },
    filterRecordMessages() {
      let q  = {
        'filters[to]': this.toContactFilters.length > 0 ? this.toContactFilters.map(c => c.id) : null,
        'filters[status]': this.messageStatusFilters.length > 0 ? this.messageStatusFilters.map(c => c.id) : null,
        'filters[tags]': this.tagsFilters.length > 0 ? this.tagsFilters.map(c => c.id) : null,
        'filters[from_sending_date]': this.fromSendingDate !== null ? this.formatDate(this.fromSendingDate) : null,
        'filters[to_sending_date]': this.toSendingDate !== null ? this.formatDate(this.toSendingDate) : null,
        'filters[search_query]': this.searchInSent.length > 2 ? this.searchInSent : null,
        'filters[starred]': this.starred !== null ? this.starred : null,
        'page': this.query.page || 1
      }
      const f = Object.entries(q).reduce((a,[k,v]) => (v === null ? a : (a[k]= v, a)), {})

      this.$refs.filtersModal.close()
      this.$router.push({ query: f })
    },
    openDeleteMessageModal(messageIds) {
      this.messageIds = messageIds
      this.$refs.deleteMessageForm.open()
    },
    deleteSentMessage() {
      this.isProcessing = true
      this.loading = true
      this.store.sendMetrics({name: 'file_communication_delete_message_to_trashbin', params: this.$route.params, path: this.$route.fullPath})
      this.store.deleteRecordMessage(this.messageIds).then(() => {
        this.bulkOpMessages = []
        this.$refs.deleteMessageForm.close()
      }).finally(() => {
        this.loading = false
        this.isProcessing = false
        this.getRecordMessages()
      })
    },
    sentTo(message){
        return this.unique(
                 this.sentMessageToNames(message).flat()
                 .concat(this.sentForwardings(message).flat())
               ).join(', ')
    },
    sentMessageToNames(message){
      let currentOrg = this.currentOrg
      let sent_messages =  message.sent_messages || []
      return sent_messages.map( (item) => {
        if(item.to_user !== undefined){
          if(item.to_user.organization_id == currentOrg.id)
            return item.to_user.full_name
        }
        if(item.to_contact !== undefined){
          if(item.to_contact.organization_id == currentOrg.id)
            return item.to_contact.name
        }
      }, [])
    },
    sentForwardings(message){
      let currentOrg = this.currentOrg
      let replies =  message.replies || []
      return replies.map( (item) => {

         let sent_messages = item.sent_messages || []
         return sent_messages.map( (sent_message) => {
             let to_user = sent_message.to_user || null
             let to_contact = sent_message.to_contact || null

           if(to_user){
             if(sent_message.to_user.organization_id == currentOrg.id)
               return to_user.full_name
           }
           if(to_contact){
             if(to_contact.organization_id == currentOrg.id)
               return to_contact.name
           }
         }, [])
      }, [])
    },
    unique(array){
      return array.filter((v, i, a) => a.indexOf(v) === i)
    },
    addTagsToBulkMessages() {
      this.store.sendMetrics({name: 'file_communication_add_tag_to_message_in_bulk', params: this.$route.params, path: this.$route.fullPath})
      this.bulkOpMessages = []
      this.getRecordMessages(false)
    },
    unreadRepliesCount(record_message_id) {
      const currentUser = this.currentUser
      return this.repliesStats.filter(item => {
        if(item.record_message_parent_id === record_message_id &&
           item.to_user_id == currentUser.id){
          return item
        }
      }).length
    },
    unreadMessagesCount(record_message_id) {
      const currentUser = this.currentUser
      return this.newMessageStats.filter(item => {
        if(item.record_message_id === record_message_id &&
           item.to_user_id == currentUser.id)
          return item
      }).length
    },
    unreadStats(id){
      return this.unreadRepliesCount(id) + this.unreadMessagesCount(id)
    },
    updateStatsCounters(){
      this.statStore.getCommunicationMessagesStats({
        'filters[to_user_id]': this.currentUser.id
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.bookmarked
  margin-left: 10px
  cursor: pointer
  font-size: 18px
  .fas
    color: #ffb429

.actions-on-table
  float: left
  width: 100%
.filter-all-messages
  form
    text-align: left
    label
      text-align: left
      display: block
    .form-group
      margin-bottom: 20px
      .date
        width: 47%
        display: inline-block
        margin-right: 10px
    .form-inline
      label
        width: 20%
        &:first-child
          width: 50%
        &:last-child
          width: 5%
          color: #686868
          cursor: pointer
.bulk-container
  background-color: #EBF4FE
  display: flex
  align-items: center
  padding: 8px 0
  border-radius: 3px
  float: left
  width: 100%
  > span
    padding-left: 10px
  ul
    list-style: none
    padding: 0
    margin: 0
    display: flex
    li
      position: relative
      a
        &:hover, &:visited, &:focus
          background: none
          text-decoration: underline
  &.hide
    display: none
.own-tooltip
  white-space: pre-wrap
.unread-messages-here
  color: #667fff
.sent_to
  white-space: normal
</style>
